import type { SearchUrlParams } from '@/types/search/SearchUrlParams';

import FacetTypes from '@/types/search/enums/facetTypes';
import paramTypes from '@/types/search/enums/paramTypes';
import { parameterNameFromFacetId } from '@/utils/queryParams';
import { buildQueryUrl, urlSafeTitle } from '@/utils/urls';

import {
    BROWSE_DIMENSION_PATH_TO_FACET_TYPE,
    BrowseCategoryRawFacet,
    BrowseDimensionPair,
    BrowsePathBase,
    buildBrowseCategoryPath,
    getBrowseDimensionKeyFromFacetType,
    getFacetOptionCandidates,
    mapBrowsePairsToSearchOptionIds,
    parseBrowseCategoryPath,
    parseLegacyCategoryPath,
    resolveFacetOptionValuesFromSemanticSlugs,
    sortBrowsePairsCanonical,
} from './browsePath';

const FACET_TYPE_TO_BROWSE_KEY: Partial<Record<string, string>> = Object.fromEntries(
    Object.entries(BROWSE_DIMENSION_PATH_TO_FACET_TYPE).map(([dim, facetType]) => [facetType, dim])
);

const DYNAMIC_SUBJECT_DIMENSION_KEY = (() => {
    const subjectDimensionKey = FACET_TYPE_TO_BROWSE_KEY[FacetTypes.ART_SUBJECT_ID];
    if (!subjectDimensionKey) {
        throw new Error('Missing browse dimension key for art subject facet type');
    }
    return subjectDimensionKey;
})();

const isSubjectLikeFacetId = (facetId: string): boolean => /subject/i.test(String(facetId));

const getDimensionKeyForFacetType = (facetType: string): string | undefined => {
    const mapped = FACET_TYPE_TO_BROWSE_KEY[facetType];
    if (mapped) {
        return mapped;
    }
    return isSubjectLikeFacetId(facetType) ? DYNAMIC_SUBJECT_DIMENSION_KEY : undefined;
};

const getFlatCategoryFacetsLikeSearchQuery = (rawFacets: BrowseCategoryRawFacet[]) =>
    rawFacets.filter(
        (f) =>
            !(
                f.id === FacetTypes.CATEGORY_ID ||
                getFacetOptionCandidates(f).length === 0 ||
                f.id === FacetTypes.AUCTION_HOUSE_ID
            )
    );

const isBrowseDimensionFacetType = (facetId: string): boolean =>
    Boolean(FACET_TYPE_TO_BROWSE_KEY[facetId]) || isSubjectLikeFacetId(facetId);

/**
 * When the same option id exists on multiple browse flat facets (e.g. taxonomy overlap),
 * `Array#find` on facet order is nondeterministic for URL building. Prefer style/period
 * over creator so period values like "Modern" map to `/style/…` in the path.
 */
const resolveBrowseFacetForOptionId = (
    rawId: string,
    flatCategoryFacets: BrowseCategoryRawFacet[]
): BrowseCategoryRawFacet | undefined => {
    const matches = flatCategoryFacets.filter(
        (f) =>
            getFacetOptionCandidates(f).some((o) => String(o.id) === String(rawId)) && isBrowseDimensionFacetType(f.id)
    );
    if (matches.length === 0) {
        return undefined;
    }
    if (matches.length === 1) {
        return matches[0];
    }
    const precedence = [
        FacetTypes.STYLE_PERIOD_ID,
        FacetTypes.MATERIALS_AND_TECHNIQUES_ID,
        FacetTypes.ART_SUBJECT_ID,
        FacetTypes.CREATOR_ID,
    ];
    for (const facetType of precedence) {
        const hit = matches.find((f) => f.id === facetType);
        if (hit) {
            return hit;
        }
    }
    return matches[0];
};

const BROWSE_FACET_TYPES_IN_PATH = new Set(Object.values(BROWSE_DIMENSION_PATH_TO_FACET_TYPE));

/** Flat browse facets that historically used `category_id=` but filters may emit semantic params (`creator=`, etc.). */
const BROWSE_FACETS_USING_CATEGORY_ID_QUERY_KEY = new Set<string>([
    FacetTypes.CREATOR_ID,
    FacetTypes.MATERIALS_AND_TECHNIQUES_ID,
    FacetTypes.STYLE_PERIOD_ID,
    FacetTypes.ORIGIN_ID,
]);

const collectMergedBrowsePairs = (
    pathPairs: BrowseDimensionPair[],
    categoryIdParam: string | undefined,
    rawFacets: BrowseCategoryRawFacet[],
    flatCategoryFacets: BrowseCategoryRawFacet[],
    queryParams?: Record<string, unknown>
): BrowseDimensionPair[] => {
    const byFacetType: Record<string, Set<string>> = {};

    const fromPath = mapBrowsePairsToSearchOptionIds(pathPairs, rawFacets);
    const maybeSubjectFacet = rawFacets.find(
        (facet) => isSubjectLikeFacetId(facet.id) && getFacetOptionCandidates(facet).length > 0
    );
    const pathSubjectPair = pathPairs.find((pair) => pair.dimensionKey === DYNAMIC_SUBJECT_DIMENSION_KEY);
    if (
        pathSubjectPair &&
        maybeSubjectFacet &&
        !fromPath[FacetTypes.ART_SUBJECT_ID]?.length &&
        !fromPath[maybeSubjectFacet.id]?.length
    ) {
        const subjectIds = pathSubjectPair.valueSlug
            .split('~')
            .filter(Boolean)
            .map((slug) => {
                const decodedSlug = decodeURIComponent(slug);
                const option = getFacetOptionCandidates(maybeSubjectFacet).find(
                    (candidate) =>
                        String(candidate.id) === decodedSlug ||
                        String(candidate.id) === slug ||
                        (candidate.name && urlSafeTitle(candidate.name) === decodedSlug)
                );
                return option?.id !== undefined ? String(option.id) : undefined;
            })
            .filter((id): id is string => Boolean(id));
        if (subjectIds.length) {
            fromPath[maybeSubjectFacet.id] = subjectIds;
        }
    }

    for (const [facetType, ids] of Object.entries(fromPath)) {
        if (!byFacetType[facetType]) {
            byFacetType[facetType] = new Set();
        }
        for (const id of ids) {
            byFacetType[facetType].add(id);
        }
    }

    if (categoryIdParam) {
        for (const rawId of categoryIdParam
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean)) {
            const facet = resolveBrowseFacetForOptionId(rawId, flatCategoryFacets);
            if (facet) {
                if (!byFacetType[facet.id]) {
                    byFacetType[facet.id] = new Set();
                }
                byFacetType[facet.id].add(rawId);
            }
        }
    }

    // e.g. Subject uses `artSubject`, not `category_id` (see parameterNameFromFacetId)
    if (queryParams) {
        for (const facetType of BROWSE_FACETS_USING_CATEGORY_ID_QUERY_KEY) {
            const semanticKey = getBrowseDimensionKeyFromFacetType(facetType);
            if (!semanticKey) {
                continue;
            }
            const rawSemantic = queryParams[semanticKey];
            if (rawSemantic === undefined || rawSemantic === null || rawSemantic === '') {
                continue;
            }
            for (const rawSegment of String(rawSemantic)
                .split(',')
                .map((segment) => segment.trim())
                .filter(Boolean)) {
                const [resolvedId] = resolveFacetOptionValuesFromSemanticSlugs(facetType, [rawSegment], rawFacets);
                const facet = resolveBrowseFacetForOptionId(resolvedId, flatCategoryFacets);
                if (facet) {
                    if (!byFacetType[facet.id]) {
                        byFacetType[facet.id] = new Set();
                    }
                    byFacetType[facet.id].add(resolvedId);
                }
            }
        }

        for (const facetType of BROWSE_FACET_TYPES_IN_PATH) {
            const paramKey = parameterNameFromFacetId(facetType);
            if (paramKey === paramTypes.CATEGORY_ID) {
                continue;
            }
            const raw = queryParams[paramKey];
            if (raw === undefined || raw === null || raw === '') {
                continue;
            }
            for (const rawId of String(raw)
                .split(',')
                .map((segment) => segment.trim())
                .filter(Boolean)) {
                const facet = resolveBrowseFacetForOptionId(rawId, flatCategoryFacets);
                if (facet) {
                    if (!byFacetType[facet.id]) {
                        byFacetType[facet.id] = new Set();
                    }
                    byFacetType[facet.id].add(rawId);
                }
            }
        }

        // Data-driven Subject may arrive as `subject` (or another subject-like facet id),
        // not necessarily the legacy `artSubject` param key.
        for (const [paramKey, rawValue] of Object.entries(queryParams)) {
            if (!isSubjectLikeFacetId(paramKey)) {
                continue;
            }
            if (rawValue === undefined || rawValue === null || rawValue === '') {
                continue;
            }
            for (const rawId of String(rawValue)
                .split(',')
                .map((s) => s.trim())
                .filter(Boolean)) {
                const facet = resolveBrowseFacetForOptionId(rawId, flatCategoryFacets);
                if (facet && isSubjectLikeFacetId(facet.id)) {
                    if (!byFacetType[facet.id]) {
                        byFacetType[facet.id] = new Set();
                    }
                    byFacetType[facet.id].add(rawId);
                }
            }
        }
    }

    const pairs: BrowseDimensionPair[] = [];
    for (const [facetType, idSet] of Object.entries(byFacetType)) {
        const dimensionKey = getDimensionKeyForFacetType(facetType);
        if (!dimensionKey || idSet.size === 0) {
            continue;
        }
        const facet = rawFacets.find((f) => f.id === facetType);
        const candidates = getFacetOptionCandidates(facet);
        const slugs = [...idSet]
            .map((id) => {
                const opt = candidates.find((o) => String(o.id) === String(id));
                if (opt?.name) {
                    return urlSafeTitle(opt.name);
                }
                return String(id);
            })
            .sort((a, b) => a.localeCompare(b));
        pairs.push({ dimensionKey, valueSlug: slugs.join('~') });
    }
    return sortBrowsePairsCanonical(pairs);
};

type TaxonomyCategoryNode = {
    categories?: { facets?: TaxonomyCategoryNode[] } | TaxonomyCategoryNode[] | null;
    id?: string | number;
    landingPageUrl?: string;
    name?: string;
    parents?: TaxonomyCategoryNode[] | null;
};

const normalizeTaxonomyCategoryChildren = (categories: TaxonomyCategoryNode['categories']): TaxonomyCategoryNode[] => {
    if (!categories) {
        return [];
    }
    if (Array.isArray(categories)) {
        return categories;
    }
    return categories.facets ?? [];
};

const findTaxonomyCategoryInParents = (
    categoryId: string | number,
    parents: TaxonomyCategoryNode[] | null | undefined
): TaxonomyCategoryNode | undefined => {
    if (!parents?.length) {
        return undefined;
    }
    for (const parent of parents) {
        if (String(parent.id) === String(categoryId)) {
            return parent;
        }
        const ancestorMatch = findTaxonomyCategoryInParents(categoryId, parent.parents);
        if (ancestorMatch) {
            return ancestorMatch;
        }
    }
    return undefined;
};

const findTaxonomyCategoryById = (
    categoryId: string | number,
    categories: TaxonomyCategoryNode['categories']
): TaxonomyCategoryNode | undefined => {
    for (const node of normalizeTaxonomyCategoryChildren(categories)) {
        if (String(node.id) === String(categoryId)) {
            return node;
        }
        const parentMatch = findTaxonomyCategoryInParents(categoryId, node.parents);
        if (parentMatch) {
            return parentMatch;
        }
        const childMatch = findTaxonomyCategoryById(categoryId, node.categories);
        if (childMatch) {
            return childMatch;
        }
    }
    return undefined;
};

const getBrowseSlugFromTaxonomyNode = (node: TaxonomyCategoryNode): string | null => {
    if (node.landingPageUrl) {
        const firstSegment = node.landingPageUrl.split('/')[0];
        if (firstSegment) {
            return firstSegment;
        }
    }
    if (node.name) {
        return urlSafeTitle(node.name);
    }
    return null;
};

const getLeafTaxonomyId = (remainingCategoryId: string): string => {
    const parts = remainingCategoryId
        .split(',')
        .map((segment) => segment.trim())
        .filter(Boolean);
    return parts[parts.length - 1] ?? remainingCategoryId;
};

/** Resolves a taxonomy (F1) category id to its browse path slug. */
export const resolveTaxonomyCategoryBrowseSlug = (
    categoryId: string | number,
    rawFacets: BrowseCategoryRawFacet[]
): string | null => {
    const categoryFacet = rawFacets.find((facet) => facet.id === FacetTypes.CATEGORY_ID);
    if (!categoryFacet?.categories?.facets?.length) {
        return null;
    }

    const node = findTaxonomyCategoryById(categoryId, categoryFacet.categories.facets);
    if (!node) {
        return null;
    }

    return getBrowseSlugFromTaxonomyNode(node as TaxonomyCategoryNode);
};

const getBrowseOnlyCategoryIdParam = (
    categoryIdParam: string | undefined,
    flatCategoryFacets: BrowseCategoryRawFacet[]
): string | undefined => {
    if (!categoryIdParam) {
        return undefined;
    }
    const kept: string[] = [];
    for (const rawId of categoryIdParam
        .split(',')
        .map((segment) => segment.trim())
        .filter(Boolean)) {
        if (resolveBrowseFacetForOptionId(rawId, flatCategoryFacets)) {
            kept.push(rawId);
        }
    }
    return kept.length ? kept.join(',') : undefined;
};

const remainingCategoryIdAfterBrowseStripped = (
    categoryIdParam: string | undefined,
    flatCategoryFacets: BrowseCategoryRawFacet[]
): string | undefined => {
    if (!categoryIdParam) {
        return undefined;
    }
    const kept: string[] = [];
    for (const rawId of categoryIdParam
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)) {
        const facet = resolveBrowseFacetForOptionId(rawId, flatCategoryFacets);
        if (facet) {
            continue;
        }
        kept.push(rawId);
    }
    return kept.length ? kept.join(',') : undefined;
};

/**
 * True when the canonical merged browse path (pathname + optional `category_id`) encodes more than
 * one selected value for any single browse dimension (same facet type), including values merged from
 * path segments (including `valueSlug` with `~` before ids resolve) and query ids.
 */
export const browseCategoryPathHasMultiSelectOnAnyDimension = (
    pathname: string,
    categoryIdParam: string | undefined,
    rawFacets: BrowseCategoryRawFacet[],
    queryParams?: Record<string, unknown>
): boolean => {
    const normalizedPath = pathname.endsWith('/') ? pathname : `${pathname}/`;
    const browseParsed = parseBrowseCategoryPath(normalizedPath);
    if (!browseParsed) {
        return false;
    }
    // URL tail encodes multi-select with `~` even when `rawFacets` cannot resolve slugs to ids yet.
    if (browseParsed.pairs.some((p) => p.valueSlug.split('~').filter(Boolean).length > 1)) {
        return true;
    }
    const flatCategoryFacets = getFlatCategoryFacetsLikeSearchQuery(rawFacets);
    const mergedPairs = collectMergedBrowsePairs(
        browseParsed.pairs,
        categoryIdParam,
        rawFacets,
        flatCategoryFacets,
        queryParams
    );
    return mergedPairs.some((p) => p.valueSlug.split('~').filter(Boolean).length > 1);
};

/** Drop only browse-dimension flat ids from `category_id`; keep taxonomy / other flat ids (path encodes browse dimensions). */
export const pruneCategoryIdQueryParamForBrowseSeoPath = <T extends Record<string, unknown>>(
    pathname: string,
    queryParams: T,
    rawFacets: BrowseCategoryRawFacet[]
): T => {
    const normalizedPath = pathname.endsWith('/') ? pathname : `${pathname}/`;
    const browseParsed = parseBrowseCategoryPath(normalizedPath);
    if (!browseParsed?.pairs?.length) {
        return queryParams;
    }
    const flat = getFlatCategoryFacetsLikeSearchQuery(rawFacets);
    let out: T = queryParams;

    const cid = queryParams.category_id;
    if (cid !== undefined && cid !== null && cid !== '') {
        const remaining = remainingCategoryIdAfterBrowseStripped(String(cid), flat);
        if (remaining === undefined) {
            const { category_id: _removed, ...rest } = out;
            out = rest as T;
        } else if (remaining !== String(cid)) {
            out = { ...out, category_id: remaining } as T;
        }
    }

    const artKey = parameterNameFromFacetId(FacetTypes.ART_SUBJECT_ID);
    const artVal = out[artKey as keyof T];
    if (artVal !== undefined && artVal !== null && artVal !== '') {
        const remainingArt = remainingCategoryIdAfterBrowseStripped(String(artVal), flat);
        if (remainingArt === undefined) {
            const { [artKey]: _removedArt, ...rest } = out as Record<string, unknown>;
            out = rest as T;
        } else if (remainingArt !== String(artVal)) {
            out = { ...(out as object), [artKey]: remainingArt } as T;
        }
    }
    for (const key of Object.keys(out as Record<string, unknown>)) {
        if (key === artKey || !isSubjectLikeFacetId(key)) {
            continue;
        }
        const val = (out as Record<string, unknown>)[key];
        if (val === undefined || val === null || val === '') {
            continue;
        }
        const remainingVal = remainingCategoryIdAfterBrowseStripped(String(val), flat);
        if (remainingVal === undefined) {
            const { [key]: _removedSubject, ...rest } = out as Record<string, unknown>;
            out = rest as T;
        } else if (remainingVal !== String(val)) {
            out = { ...(out as object), [key]: remainingVal } as T;
        }
    }

    return out;
};

/**
 * Builds category / price-guide URLs with SEO browse dimensions in the path and
 * taxonomy (and non-browse flat facet) ids left in `category_id`.
 */
export const getCategoryBrowseNavigateUrl = (
    pathname: string,
    params: SearchUrlParams & Record<string, unknown>,
    rawFacets: BrowseCategoryRawFacet[]
): string => {
    const normalizedPath = pathname.endsWith('/') ? pathname : `${pathname}/`;
    const browseParsed = parseBrowseCategoryPath(normalizedPath);
    const legacyParsed = browseParsed ? null : parseLegacyCategoryPath(normalizedPath);

    if (!browseParsed && !legacyParsed) {
        return buildQueryUrl(normalizedPath, params);
    }

    let base: BrowsePathBase;
    let categorySlug: string;
    const pathPairs = browseParsed?.pairs ?? [];

    if (browseParsed) {
        base = browseParsed.base;
        categorySlug = browseParsed.categorySlug;
    } else if (legacyParsed) {
        base = legacyParsed.base;
        categorySlug = legacyParsed.categorySlug;
    } else {
        return buildQueryUrl(normalizedPath, params);
    }

    const flatCategoryFacets = getFlatCategoryFacetsLikeSearchQuery(rawFacets);
    const categoryIdParam = params.category_id?.toString();
    const remainingCategoryId = remainingCategoryIdAfterBrowseStripped(categoryIdParam, flatCategoryFacets);

    let effectiveCategorySlug = categorySlug;
    let effectivePathPairs = pathPairs;
    let categoryIdParamForMergedPairs = categoryIdParam;
    let resolvedTaxonomySlug: string | null = null;

    if (browseParsed && remainingCategoryId) {
        resolvedTaxonomySlug = resolveTaxonomyCategoryBrowseSlug(getLeafTaxonomyId(remainingCategoryId), rawFacets);
        if (resolvedTaxonomySlug) {
            categoryIdParamForMergedPairs = getBrowseOnlyCategoryIdParam(categoryIdParam, flatCategoryFacets);
            if (resolvedTaxonomySlug !== categorySlug) {
                effectiveCategorySlug = resolvedTaxonomySlug;
                effectivePathPairs = [];
            }
        }
    }

    const mergedPairs = collectMergedBrowsePairs(
        effectivePathPairs,
        categoryIdParamForMergedPairs,
        rawFacets,
        flatCategoryFacets,
        params
    );

    const nextParams = { ...params };
    if (browseParsed && remainingCategoryId && resolvedTaxonomySlug) {
        delete nextParams.category_id;
    } else if (remainingCategoryId !== undefined) {
        nextParams.category_id = remainingCategoryId;
    } else {
        delete nextParams.category_id;
    }

    const artSubjectKey = parameterNameFromFacetId(FacetTypes.ART_SUBJECT_ID);
    const artSubjectRaw = params[artSubjectKey];
    if (artSubjectRaw !== undefined && artSubjectRaw !== null && artSubjectRaw !== '') {
        const remainingArtSubject = remainingCategoryIdAfterBrowseStripped(String(artSubjectRaw), flatCategoryFacets);
        if (remainingArtSubject !== undefined) {
            nextParams[artSubjectKey] = remainingArtSubject;
        } else {
            delete nextParams[artSubjectKey];
        }
    }
    for (const [key, value] of Object.entries(params)) {
        if (key === artSubjectKey || !isSubjectLikeFacetId(key)) {
            continue;
        }
        if (value === undefined || value === null || value === '') {
            continue;
        }
        const remainingSubject = remainingCategoryIdAfterBrowseStripped(String(value), flatCategoryFacets);
        if (remainingSubject !== undefined) {
            nextParams[key] = remainingSubject;
        } else {
            delete nextParams[key];
        }
    }

    const mergedDimensionKeys = new Set(mergedPairs.map((pair) => pair.dimensionKey));
    for (const facetType of BROWSE_FACETS_USING_CATEGORY_ID_QUERY_KEY) {
        const semanticKey = getBrowseDimensionKeyFromFacetType(facetType);
        if (semanticKey && mergedDimensionKeys.has(semanticKey)) {
            delete nextParams[semanticKey];
        }
    }

    if (mergedPairs.length === 0) {
        if (legacyParsed) {
            const legacyPath = `${legacyParsed.base}/${legacyParsed.categorySlug}/${legacyParsed.numericCategoryId}/`;
            return buildQueryUrl(legacyPath, nextParams);
        }
        const slugOnly = `${base}/${effectiveCategorySlug}/`;
        return buildQueryUrl(slugOnly, nextParams);
    }

    const browsePath = buildBrowseCategoryPath(effectiveCategorySlug, mergedPairs, { base });
    return buildQueryUrl(browsePath, nextParams);
};
